export const HELPSCOUT_BEACON_SCRIPT_SRC = 'https://beacon-v2.helpscout.net'
export const HELPSCOUT_BEACON_SCRIPT_ID = 'helpscout-beacon-v2'

type BeaconReadyQueueItem = {
  method: string
  options?: unknown
  data?: unknown
}

export type HelpScoutBeaconApi = {
  (method: string, options?: unknown, data?: unknown): void
  readyQueue?: BeaconReadyQueueItem[]
}

type HelpScoutBeaconConfig = {
  display?: {
    style?: 'icon' | 'text' | 'iconAndText' | 'manual'
    position?: 'left' | 'right'
  }
}

declare global {
  interface Window {
    Beacon?: HelpScoutBeaconApi
    __shopexpertsHelpScoutBeaconId?: string
  }
}

function insertBeaconScriptIfMissing(): void {
  const existingById = document.getElementById(HELPSCOUT_BEACON_SCRIPT_ID)
  if (existingById) return

  // Avoid duplicates if something injected it without our ID
  const existingBySrc = Array.from(document.getElementsByTagName('script')).some(
    (s) => s.src === HELPSCOUT_BEACON_SCRIPT_SRC
  )
  if (existingBySrc) return

  const firstScript = document.getElementsByTagName('script')[0]
  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.async = true
  script.src = HELPSCOUT_BEACON_SCRIPT_SRC
  script.id = HELPSCOUT_BEACON_SCRIPT_ID

  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript)
  } else {
    document.head.appendChild(script)
  }
}

function ensureBeaconStubInstalled(): HelpScoutBeaconApi {
  // The loaded SDK replaces the queue stub with its real API.
  if (typeof window.Beacon === 'function') {
    return window.Beacon
  }

  const stub = ((method: string, options?: unknown, data?: unknown) => {
    stub.readyQueue?.push({ method, options, data })
  }) as HelpScoutBeaconApi
  stub.readyQueue = []

  window.Beacon = stub
  return stub
}

function updateBeaconDisplayStyle(style: 'icon' | 'text' | 'iconAndText' | 'manual'): void {
  const config: HelpScoutBeaconConfig = {
    display: {
      style,
      position: 'right',
    },
  }

  window.Beacon?.('config', config)
}

/**
 * Installs the HelpScout Beacon loader and initializes it with the given beaconId.
 *
 * - Safe to call multiple times (per-app singleton)
 * - No-ops during SSR
 */
export function initHelpScoutBeacon(beaconId: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  if (!beaconId) return

  ensureBeaconStubInstalled()

  // Match the official snippet behavior: inject script on window load (or immediately if already loaded).
  if (document.readyState === 'complete') {
    insertBeaconScriptIfMissing()
  } else {
    window.addEventListener('load', insertBeaconScriptIfMissing, { once: true })
  }

  if (window.__shopexpertsHelpScoutBeaconId !== beaconId) {
    window.__shopexpertsHelpScoutBeaconId = beaconId
    window.Beacon?.('init', beaconId)
  }
}

export function setHelpScoutBeaconHidden(hidden: boolean): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  ensureBeaconStubInstalled()
  if (hidden) {
    window.Beacon?.('close')
    updateBeaconDisplayStyle('manual')
    return
  }

  updateBeaconDisplayStyle('icon')
}
